/**
 * useBootstrapEvents
 *
 * Hook responsável por escutar EVENTOS CUSTOMIZADOS emitidos pelo MessageRouter.
 * 
 * ARQUITETURA PERFEITA:
 * - MessageRouter é o ÚNICO listener do window.chrome.webview
 * - MessageRouter emite eventos customizados para cada tipo de mensagem
 * - Este hook escuta esses eventos customizados (não WebView2 diretamente)
 * 
 * EVENTOS ESCUTADOS:
 * - session:token-received → Tokens de autenticação
 * - bootstrap:received → Dados de bootstrap da sessão
 * - bootstrap:ack-received → Confirmação de bootstrap com snapshot
 * - navigate:to-tool → Navegação para ferramenta específica
 * - navigate:to-url → Navegação para URL externa
 * 
 * IMPORTANTE:
 * - NÃO escuta shortcuts (processados separadamente em ToolsMenu.tsx)
 * - NÃO registra listeners no WebView2 (responsabilidade do MessageRouter)
 * 
 * @see docs/SHORTCUTS_ARCHITECTURE.md para fluxo completo
 * @author Refatoração Arquitetural 2026-01-18
 */

import { useEffectOnce } from '@/hooks/useEffectOnce';
import { sendToTerminal, logger as terminalLog } from '@/lib/terminalLogger';
import { handleBootstrapData, type BootstrapData } from '@/lib/corel/bootstrapHandler';
import { corelCommunication } from '@/lib/corel/communication';
import { setToolsSessionReady } from '@/lib/corel/toolsSession';
import { TokenService } from '@/lib/tokenService';
import { processSessionToken } from '../utils/sessionTokenProcessor';

const SESSION_APPLIED_RETRY_DELAYS_MS = [150, 400, 900];

export interface BootstrapEventsCallbacks {
  /** Callback para navegação para ferramenta específica */
  onNavigateToTool?: (toolId: string) => void;
  /** Callback para navegação para URL */
  onNavigateToUrl?: (url: string) => void;
}

/**
 * Hook para escutar eventos de bootstrap/navegação emitidos pelo MessageRouter
 *
 * Escuta APENAS eventos customizados (window.addEventListener), não WebView2.
 * MessageRouter é o único que escuta window.chrome.webview.
 */
export function useBootstrapEvents(callbacks?: BootstrapEventsCallbacks) {
  useEffectOnce(() => {
    const sessionAppliedRetryTimers = new Set<number>();

    setToolsSessionReady(false, 'bootstrap-hook-mounted', {
      dockerId: (window as any).dockerId || null
    });

    const syncDockerIdFromResponse = (detail: any, response: any, accepted: boolean) => {
      if (!accepted) {
        return;
      }

      const responseDockerId = response?.result?.dockerId;
      if (
        typeof responseDockerId === 'string' &&
        responseDockerId &&
        responseDockerId !== (window as any).dockerId
      ) {
        sendToTerminal('🔄 [BOOTSTRAP_EVENTS] dockerId sincronizado pelo ACK do C#', {
          previousDockerId: (window as any).dockerId || null,
          nextDockerId: responseDockerId,
          responseStatus: response?.status,
          responseMessage: response?.message || null,
          detailDockerId: detail?.dockerId || null
        });
        (window as any).dockerId = responseDockerId;
      }
    };

    const scheduleSessionAppliedRetry = (detail: any, attempt: number, response: any) => {
      if (attempt >= SESSION_APPLIED_RETRY_DELAYS_MS.length) {
        return false;
      }

      const delayMs = SESSION_APPLIED_RETRY_DELAYS_MS[attempt];
      setToolsSessionReady(false, 'session-applied-retry-scheduled', {
        dockerId: detail.dockerId || (window as any).dockerId,
        sessionEpoch: detail.sessionEpoch || 0,
        tokenVersion: detail.tokenVersion || 0,
        responseStatus: response?.status || null,
        responseMessage: response?.message || null,
        retryAttempt: attempt + 1,
        retryDelayMs: delayMs
      });

      sendToTerminal('🔁 [BOOTSTRAP_EVENTS] Reagendando tools:session-applied', {
        dockerId: detail.dockerId || (window as any).dockerId,
        sessionEpoch: detail.sessionEpoch || 0,
        tokenVersion: detail.tokenVersion || 0,
        responseStatus: response?.status || null,
        responseMessage: response?.message || null,
        responseReason: response?.result?.reason || null,
        retryAttempt: attempt + 1,
        retryDelayMs: delayMs
      });

      const timerId = window.setTimeout(() => {
        sessionAppliedRetryTimers.delete(timerId);
        sendSessionApplied(detail, attempt + 1);
      }, delayMs);

      sessionAppliedRetryTimers.add(timerId);
      return true;
    };

    const sendSessionApplied = (detail: any, attempt = 0) => {
      try {
        setToolsSessionReady(false, 'session-applied-pending-ack', {
          dockerId: detail.dockerId || (window as any).dockerId,
          sessionEpoch: detail.sessionEpoch || 0,
          tokenVersion: detail.tokenVersion || 0,
          attempt: attempt + 1
        });

        sendToTerminal('📤 [BOOTSTRAP_EVENTS] Enviando tools:session-applied', {
          dockerId: detail.dockerId || (window as any).dockerId,
          sessionEpoch: detail.sessionEpoch || 0,
          tokenVersion: detail.tokenVersion || 0,
          processingSuccess: true,
          attempt: attempt + 1
        });

        const requestId = corelCommunication.sendActionGuarded('tools:session-applied', {
          dockerId: detail.dockerId || (window as any).dockerId,
          sessionEpoch: detail.sessionEpoch || 0,
          tokenVersion: detail.tokenVersion || 0,
          issuedAt: detail.issuedAt || new Date().toISOString()
        }, (response) => {
          const responseStatus = response?.status;
          const responseAccepted = typeof response?.result?.accepted === 'boolean'
            ? response.result.accepted
            : null;
          const responseReason = response?.result?.reason || response?.message || null;
          const accepted = responseAccepted === true
            || (
              responseAccepted === null
              && responseStatus !== 'error'
              && responseStatus !== 'erro'
              && responseStatus !== 'timeout'
            );

          syncDockerIdFromResponse(detail, response, accepted);

          sendToTerminal('📥 [BOOTSTRAP_EVENTS] Resposta de tools:session-applied recebida', {
            dockerId: detail.dockerId || (window as any).dockerId,
            requestId: response?.requestId,
            status: responseStatus,
            message: response?.message,
            result: response?.result,
            accepted,
            resolutionSource: response?.result?.resolutionSource || null,
            attempt: attempt + 1
          });

          if (accepted) {
            setToolsSessionReady(true, 'session-applied-ack', {
              dockerId: detail.dockerId || (window as any).dockerId,
              sessionEpoch: detail.sessionEpoch || 0,
              tokenVersion: detail.tokenVersion || 0,
              responseStatus,
              responseMessage: response?.message || null
            });
            return;
          }

          const retryable = responseReason === 'tools_docker_not_found'
            || responseReason === 'tools_docker_pending';
          if (retryable && scheduleSessionAppliedRetry(detail, attempt, response)) {
            return;
          }

          setToolsSessionReady(false, 'session-applied-rejected', {
            dockerId: detail.dockerId || (window as any).dockerId,
            sessionEpoch: detail.sessionEpoch || 0,
            tokenVersion: detail.tokenVersion || 0,
            responseStatus,
            responseMessage: response?.message || null,
            responseReason,
            attempt: attempt + 1
          });
        });

        sendToTerminal('✅ [BOOTSTRAP_EVENTS] tools:session-applied enviado', {
          dockerId: detail.dockerId || (window as any).dockerId,
          requestId,
          attempt: attempt + 1
        });
      } catch (error) {
        setToolsSessionReady(false, 'session-applied-send-error', {
          dockerId: detail.dockerId || (window as any).dockerId,
          sessionEpoch: detail.sessionEpoch || 0,
          tokenVersion: detail.tokenVersion || 0,
          error: error instanceof Error ? error.message : String(error),
          attempt: attempt + 1
        });
        sendToTerminal('❌ [BOOTSTRAP_EVENTS] Erro ao enviar tools:session-applied', {
          dockerId: detail.dockerId,
          error: error instanceof Error ? error.message : String(error),
          attempt: attempt + 1
        });
      }
    };

    const handleBootstrap = async (rawData: any) => {
      try {
        const bootstrapData = rawData?.data || rawData?.bootstrap;

        if (!bootstrapData) {
          terminalLog.warn('docker-tools:bootstrap-empty', { rawData });
          return;
        }

        // Usar handler centralizado para processar bootstrap (aguarda permissoes)
        const result = await handleBootstrapData(bootstrapData as BootstrapData, 'event');

        if (result.success) {
          terminalLog.debug('docker-tools:bootstrap-processed', { source: 'event' });
        } else {
          sendToTerminal('❌ [BOOTSTRAP_EVENTS] Erro ao processar bootstrap', {
            error: result.error
          });
        }
      } catch (e) {
        sendToTerminal('❌ [BOOTSTRAP_EVENTS] Erro ao processar bootstrap', {
          error: e instanceof Error ? e.message : String(e),
          stack: e instanceof Error ? e.stack : undefined
        });
      }
    };

    const sendReadyForSession = () => {
      const dockerId = (window as any).dockerId;
      /*
      if (!processingResult.success) {
        sendToTerminal('â­ï¸ [BOOTSTRAP_EVENTS] tools:session-applied nÃ£o enviado', {
          dockerId: detail.dockerId || (window as any).dockerId,
          sessionEpoch: detail.sessionEpoch || 0,
          tokenVersion: detail.tokenVersion || 0,
          reason: 'processSessionToken_failed',
          tokenStored: processingResult.tokenStored,
          hmacStored: processingResult.hmacStored
        });
        return;
      }

      */
      try {
        setToolsSessionReady(false, 'ready-for-session-sent', {
          dockerId,
          sessionEpoch: Number((window as any)?.sessionEpoch || 0),
          tokenVersion: Number((window as any)?.tokenVersion || 0)
        });

        sendToTerminal('📤 [BOOTSTRAP_EVENTS] Enviando tools:ready-for-session', {
          dockerId,
          hasWebView: !!(window as any)?.chrome?.webview,
          hasSessionToken: !!(window as any)?.sessionToken,
          hasHmacKey: !!(window as any)?.sessionCrypto?.hmacKeyB64,
          sessionEpoch: Number((window as any)?.sessionEpoch || 0),
          tokenVersion: Number((window as any)?.tokenVersion || 0)
        });

        const requestId = corelCommunication.sendActionGuarded('tools:ready-for-session', {
          dockerId,
          ts: Date.now()
        });

        sendToTerminal('✅ [BOOTSTRAP_EVENTS] tools:ready-for-session enviado', {
          dockerId,
          requestId
        });
      } catch (error) {
        sendToTerminal('❌ [BOOTSTRAP_EVENTS] Erro ao enviar tools:ready-for-session', {
          dockerId,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // LISTENERS DE EVENTOS CUSTOMIZADOS (emitidos pelo MessageRouter)
    // ═══════════════════════════════════════════════════════════════════════════

    // Handler para session:token-received
    const handleSessionToken = (event: Event) => {
      const customEvent = event as CustomEvent<{
        sessionToken: string;
        hmacKeyB64: string;
        source: string;
        dockerId?: string;
        sessionEpoch?: number;
        tokenVersion?: number;
      }>;
      const { sessionToken, hmacKeyB64, source } = customEvent.detail || {};
      
      if (sessionToken && hmacKeyB64) {
        processSessionToken(sessionToken, hmacKeyB64, source, {
          dockerId: customEvent.detail?.dockerId,
          sessionEpoch: customEvent.detail?.sessionEpoch,
          tokenVersion: customEvent.detail?.tokenVersion
        });
      }
    };

    const replayBufferedToolsBootstrap = (reason: string) => {
      const buffered = (window as any).__ONEPRINT_LAST_TOOLS_BOOTSTRAP__;
      if (!buffered?.sessionToken || !buffered?.hmacKeyB64) {
        sendToTerminal('ℹ️ [BOOTSTRAP_EVENTS] Nenhum bootstrap buffered para replay', {
          reason,
          hasBuffered: !!buffered
        });
        return;
      }

      sendToTerminal('🔁 [BOOTSTRAP_EVENTS] Reaplicando bootstrap buffered do tools', {
        reason,
        dockerId: buffered.dockerId,
        sessionEpoch: buffered.sessionEpoch,
        tokenVersion: buffered.tokenVersion,
        source: buffered.source,
        capturedAt: buffered.capturedAt
      });

      handleToolsSessionBootstrap(new CustomEvent('tools:session-bootstrap-received', {
        detail: buffered
      }));
    };

    const handleToolsSessionBootstrap = (event: Event) => {
      const customEvent = event as CustomEvent<{
        dockerId?: string;
        sessionEpoch?: number;
        tokenVersion?: number;
        sessionToken?: string;
        hmacKeyB64?: string;
        frontendUrl?: string;
        issuedAt?: string;
        source?: 'webview' | 'window';
      }>;

      const detail = customEvent.detail || {};
      sendToTerminal('🚀 [BOOTSTRAP_EVENTS] handleToolsSessionBootstrap iniciado', {
        dockerId: detail.dockerId,
        sessionEpoch: detail.sessionEpoch,
        tokenVersion: detail.tokenVersion,
        hasToken: !!detail.sessionToken,
        hasHmacKey: !!detail.hmacKeyB64,
        currentDockerId: (window as any).dockerId,
        currentSessionEpoch: Number((window as any).sessionEpoch || 0),
        currentTokenVersion: Number((window as any).tokenVersion || 0)
      });

      const processingResult = processSessionToken(detail.sessionToken, detail.hmacKeyB64, detail.source || 'webview', {
        dockerId: detail.dockerId,
        sessionEpoch: detail.sessionEpoch,
        tokenVersion: detail.tokenVersion
      });

      sendToTerminal('📊 [BOOTSTRAP_EVENTS] processSessionToken concluído', {
        dockerId: detail.dockerId,
        sessionEpoch: detail.sessionEpoch,
        tokenVersion: detail.tokenVersion,
        success: processingResult.success,
        tokenStored: processingResult.tokenStored,
        hmacStored: processingResult.hmacStored,
        currentDockerId: (window as any).dockerId,
        currentSessionEpoch: Number((window as any).sessionEpoch || 0),
        currentTokenVersion: Number((window as any).tokenVersion || 0),
        hasWindowToken: !!(window as any).sessionToken,
        hasWindowHmac: !!(window as any)?.sessionCrypto?.hmacKeyB64
      });

      if (!processingResult.success) {
        setToolsSessionReady(false, 'processSessionToken_failed', {
          dockerId: detail.dockerId || (window as any).dockerId,
          sessionEpoch: detail.sessionEpoch || 0,
          tokenVersion: detail.tokenVersion || 0,
          tokenStored: processingResult.tokenStored,
          hmacStored: processingResult.hmacStored
        });
        sendToTerminal('⏭️ [BOOTSTRAP_EVENTS] tools:session-applied não enviado', {
          dockerId: detail.dockerId || (window as any).dockerId,
          sessionEpoch: detail.sessionEpoch || 0,
          tokenVersion: detail.tokenVersion || 0,
          reason: 'processSessionToken_failed',
          tokenStored: processingResult.tokenStored,
          hmacStored: processingResult.hmacStored
        });
        return;
      }

      try {
        window.dispatchEvent(new CustomEvent('embedded:auth-reconcile', {
          detail: {
            reason: 'tools-session-bootstrap',
            token: TokenService.getAccessToken() || undefined,
            sessionEpoch: detail.sessionEpoch || 0,
            tokenVersion: detail.tokenVersion || 0
          }
        }));
      } catch {}

      sendSessionApplied(detail);
    };

    // Handler para bootstrap:received
    const handleBootstrapReceived = (event: Event) => {
      const customEvent = event as CustomEvent<{
        data: any;
        rawData: any;
      }>;
      const { rawData } = customEvent.detail || {};
      
      if (rawData) {
        handleBootstrap(rawData);
      }
    };

    // Handler para bootstrap:ack-received
    const handleBootstrapAck = (event: Event) => {
      const customEvent = event as CustomEvent<{
        snapshot: any;
      }>;
      const { snapshot } = customEvent.detail || {};
      
      if (snapshot) {
        handleBootstrap({ data: snapshot });
      }
    };

    // Handler para navigate:to-tool
    const handleNavigateToTool = (event: Event) => {
      const customEvent = event as CustomEvent<{
        toolId: string;
      }>;
      const { toolId } = customEvent.detail || {};
      
      if (toolId && callbacks?.onNavigateToTool) {
        callbacks.onNavigateToTool(toolId);
      }
    };

    // Handler para navigate:to-url
    const handleNavigateToUrl = (event: Event) => {
      const customEvent = event as CustomEvent<{
        url: string;
      }>;
      const { url } = customEvent.detail || {};
      
      if (url) {
        if (callbacks?.onNavigateToUrl) {
          callbacks.onNavigateToUrl(url);
        } else {
          // Fallback: navegar diretamente
          window.location.href = url;
        }
      }
    };

    const handleSessionReinjected = () => {
      sendReadyForSession();
      replayBufferedToolsBootstrap('session-reinjected');
      window.setTimeout(() => replayBufferedToolsBootstrap('session-reinjected-delayed'), 200);
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // REGISTRAR LISTENERS DE EVENTOS CUSTOMIZADOS
    // ═══════════════════════════════════════════════════════════════════════════

    window.addEventListener('session:token-received', handleSessionToken);
    window.addEventListener('tools:session-bootstrap-received', handleToolsSessionBootstrap);
    window.addEventListener('bootstrap:received', handleBootstrapReceived);
    window.addEventListener('bootstrap:ack-received', handleBootstrapAck);
    window.addEventListener('navigate:to-tool', handleNavigateToTool);
    window.addEventListener('navigate:to-url', handleNavigateToUrl);
    window.addEventListener('oneprint:session-reinjected', handleSessionReinjected);

    replayBufferedToolsBootstrap('after-listeners-registered');
    sendReadyForSession();
    window.setTimeout(() => replayBufferedToolsBootstrap('post-ready-for-session'), 200);

    // Verificar window.__ONEPRINT_BOOTSTRAP__ (injeção via ExecuteScriptAsync)
    if ((window as any).__ONEPRINT_BOOTSTRAP__) {
      handleBootstrap({ bootstrap: (window as any).__ONEPRINT_BOOTSTRAP__ });
      delete (window as any).__ONEPRINT_BOOTSTRAP__;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════════════════

    return () => {
      sessionAppliedRetryTimers.forEach((timerId) => window.clearTimeout(timerId));
      sessionAppliedRetryTimers.clear();
      window.removeEventListener('session:token-received', handleSessionToken);
      window.removeEventListener('tools:session-bootstrap-received', handleToolsSessionBootstrap);
      window.removeEventListener('bootstrap:received', handleBootstrapReceived);
      window.removeEventListener('bootstrap:ack-received', handleBootstrapAck);
      window.removeEventListener('navigate:to-tool', handleNavigateToTool);
      window.removeEventListener('navigate:to-url', handleNavigateToUrl);
      window.removeEventListener('oneprint:session-reinjected', handleSessionReinjected);
    };
  });
}
